import React, { useMemo } from 'react';

import { colors } from '@/assets/styles/colors';
import { EmployeeDimensionalFilter } from '@/components/features/attendance/EmployeeDimensionalFilter';
import { PermissionGuard } from '@/components/layout';
import PageLayout from '@/components/layout/PageLayout';
import { Banner } from '@/components/ui/banners';
import { IndicatorCard } from '@/components/ui/cards';
import { DynamicFiltersCard } from '@/components/ui/cards/DynamicFiltersCard';
import type { FilterField } from '@/components/ui/cards/DynamicFiltersCard';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { IconScout } from '@/components/ui/images/IconScout';
import { Selector } from '@/components/ui/inputs/selector';
import { Switch } from '@/components/ui/inputs';
import Table from '@/components/ui/tables/Table';
import { Text } from '@/components/ui/text/Text';
import { OVERTIME_COUNTER_SLOTS } from '@/hooks/overtime/overtimeApprovalCounters';
import { useOvertimeApproval } from '@/hooks/overtime/useOvertimeApproval';
import { useTranslation } from '@/i18n';


export const OvertimeApproval: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    isLoading,
    onSearch,
    onSortChange,
    companyOptions,
    selectedCompanyValue,
    onCompanyChange,
    yearOptions,
    monthOptions,
    descriptionOptions,
    selectedYear,
    selectedMonth,
    selectedMonthlyCutId,
    onYearChange,
    onMonthChange,
    onDescriptionChange,
    isLoadingCuts,
    arePeriodFiltersDisabled,
    noCutsMessage,
    counters,
    onCounterSelect,
    isDecimalFormat,
    onHourFormatToggle,
    dimensionalConfig,
    dimensionalFilter,
    isAdvancedFiltersDisabled,
    isCompanyLocked,
    canDownload,
    isSelectable,
    bulkActions,
    onSelectionChange,
    isRowSelectable,
    selectedRowIds,
    closedCutMessage,
    rowActions,
  } = useOvertimeApproval();

  const filterFields: FilterField[] = useMemo(() => [
    {
      id: 'company',
      type: 'custom',
      colSpan: 1,
      loading: companyOptions.length === 0,
      component: (
        <Selector
          label={translate('attendance:overtimeApproval.filters.company')}
          value={selectedCompanyValue}
          onChange={(value) => onCompanyChange(Array.isArray(value) ? (value[0] ?? '') : value)}
          options={companyOptions}
          placeholder={translate('attendance:overtimeApproval.filters.selectCompany')}
          data-testid="overtime-approval-company-selector"
        />
      ),
    },
    {
      id: 'year',
      type: 'custom',
      colSpan: 1,
      loading: isLoadingCuts,
      component: (
        <Selector
          label={translate('attendance:overtimeApproval.filters.year')}
          value={selectedYear !== null ? String(selectedYear) : ''}
          onChange={(value) => onYearChange(Array.isArray(value) ? (value[0] ?? '') : value)}
          options={yearOptions}
          disabled={arePeriodFiltersDisabled}
          placeholder={translate('attendance:overtimeApproval.filters.selectYear')}
          data-testid="overtime-approval-year-selector"
        />
      ),
    },
    {
      id: 'month',
      type: 'custom',
      colSpan: 1,
      loading: isLoadingCuts,
      component: (
        <Selector
          label={translate('attendance:overtimeApproval.filters.month')}
          value={selectedMonth !== null ? String(selectedMonth) : ''}
          onChange={(value) => onMonthChange(Array.isArray(value) ? (value[0] ?? '') : value)}
          options={monthOptions}
          disabled={arePeriodFiltersDisabled}
          placeholder={translate('attendance:overtimeApproval.filters.selectMonth')}
          data-testid="overtime-approval-month-selector"
        />
      ),
    },
    {
      id: 'description',
      type: 'custom',
      colSpan: 1,
      loading: isLoadingCuts,
      component: (
        <Selector
          label={translate('attendance:overtimeApproval.filters.description')}
          value={selectedMonthlyCutId !== null ? String(selectedMonthlyCutId) : ''}
          onChange={(value) => onDescriptionChange(Array.isArray(value) ? (value[0] ?? '') : value)}
          options={descriptionOptions}
          disabled={arePeriodFiltersDisabled}
          placeholder={translate('attendance:overtimeApproval.filters.selectDescription')}
          data-testid="overtime-approval-description-selector"
        />
      ),
    },
  ], [
    translate,
    companyOptions,
    selectedCompanyValue,
    onCompanyChange,
    yearOptions,
    monthOptions,
    descriptionOptions,
    selectedYear,
    selectedMonth,
    selectedMonthlyCutId,
    onYearChange,
    onMonthChange,
    onDescriptionChange,
    isLoadingCuts,
    arePeriodFiltersDisabled,
  ]);

  /* Aviso permanente mientras el corte seleccionado esté cerrado: además de este mensaje, la
     selección de filas y la acción de aprobar quedan deshabilitadas (ASIS-5353). Va en el slot
     `banner` de PageLayout, que lo renderiza a ancho completo fuera de la tarjeta principal.
     No es descartable a propósito: el criterio de aceptación exige mantenerlo visible. */
  const closedCutNotice = closedCutMessage && (
    <Banner
      variant="info"
      density="compact"
      rounded={false}
      className="px-4 md:px-8 lg:px-16"
      text={closedCutMessage}
      data-testid="overtime-approval-closed-cut-message"
    />
  );

  return (
    <PageLayout banner={closedCutNotice}>
      <div className="flex flex-col gap-6" data-testid="overtime-approval-list-page">
        <div className="flex flex-col gap-4">
          <div className="flex justify-end">
            <Switch
              checked={isDecimalFormat}
              onChange={onHourFormatToggle}
              label={translate('attendance:overtimeApproval.decimalSwitch.label')}
              tooltipText={translate('attendance:overtimeApproval.decimalSwitch.tooltip')}
              data-testid="overtime-approval-decimal-switch"
            />
          </div>

          <div
            className="flex flex-row flex-wrap gap-4"
            data-testid="overtime-approval-counters"
          >
            {counters.map((counter) => (
              <IndicatorCard
                key={counter.key}
                label={counter.label}
                value={counter.value}
                padValue={false}
                tooltipText={counter.tooltip}
                variant={counter.variant}
                isSelected={counter.isSelected}
                onClick={() => onCounterSelect(counter.key)}
                data-testid={`overtime-approval-counter-${counter.key}`}
              />
            ))}
            {/* Las tarjetas son `flex-1`, así que un set corto (el revisor ve tres) se estiraría
                hasta llenar la fila. Los espaciadores ocupan los lugares sobrantes para que el
                hueco quede al final y el ancho de cada tarjeta no dependa del perfil. */}
            {Array.from(
              { length: Math.max(0, OVERTIME_COUNTER_SLOTS - counters.length) },
              (_unused, index) => (
                <div
                  key={`overtime-approval-counter-slot-${index}`}
                  className="flex-1 min-w-[180px]"
                  aria-hidden="true"
                />
              ),
            )}
          </div>
        </div>

        <DynamicFiltersCard
          fields={filterFields}
          columns={4}
          footer={(
            <div className="flex flex-col gap-4">
              {noCutsMessage && (
                <Text
                  variant="body-regular"
                  color={colors.feedback.error300}
                  data-testid="overtime-approval-no-cuts-message"
                >
                  {noCutsMessage}
                </Text>
              )}

              <button
                type="button"
                onClick={dimensionalFilter.toggleExpanded}
                disabled={isAdvancedFiltersDisabled}
                className="self-start flex items-center gap-1 font-medium text-sm cursor-pointer bg-transparent border-none p-0 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ color: colors.important.main }}
                aria-expanded={dimensionalFilter.isExpanded}
                data-testid="overtime-approval-advanced-filters-toggle"
              >
                {dimensionalFilter.isExpanded
                  ? translate('attendance:dimensionalFilter.hide')
                  : translate('attendance:dimensionalFilter.showAdvancedFilters')}
                <IconScout name={dimensionalFilter.isExpanded ? 'angleUp' : 'angleDown'} size={16} />
              </button>

              {dimensionalFilter.isExpanded && (
                /* Sin botón Buscar: los filtros avanzados se aplican solos al cambiar
                   (ASIS-5352). */
                <EmployeeDimensionalFilter
                  config={dimensionalConfig}
                  state={dimensionalFilter}
                  companyLocked={isCompanyLocked}
                />
              )}
            </div>
          )}
        />

        <PermissionGuard action="view">
          <div className="flex justify-end">
            {/* La descarga efectiva queda fuera del alcance de ASIS-5351: aquí solo se respeta
                la regla de habilitación del botón descrita en los criterios de aceptación. */}
            <ExportDropdown
              onExport={() => undefined}
              placement="right"
              disabled={!canDownload}
              availableFormats={['EXCEL']}
            />
          </div>
        </PermissionGuard>

        <Table
          columns={columns}
          data={rows}
          totalRows={total}
          isLoading={isLoading}
          serverSideSort
          onSortChange={onSortChange}
          onSearch={onSearch}
          searchPlaceholder={translate('common:table.searchPlaceholder')}
          /* Sin <PermissionGuard> aquí a propósito: Table es genérico (Regla 8) y no tiene un slot
             para envolver solo su columna de selección o su menú de acciones masivas sin ocultar el
             listado completo a usuarios de solo lectura. isSelectable ya deriva de can('edit') y la
             mutación real vuelve a chequearlo en handleBulkApprove/approveSelection. */
          selectable={isSelectable}
          bulkActions={bulkActions}
          onSelectionChange={onSelectionChange}
          selectedRowIds={selectedRowIds}
          isRowSelectable={isRowSelectable}
          actions={rowActions}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          data-testid="overtime-approval-table"
        />
      </div>
    </PageLayout>
  );
};

export default OvertimeApproval;
